import React, { useState } from 'react';
import { Mail, MapPin, Phone, Instagram } from 'lucide-react';
import CustomerTermsModal from './CustomerTermsModal';

interface FooterProps {
  storeName?: string;
  phone?: string;
  email?: string;
  address?: string;
  cnpj?: string;
  primaryColor?: string;
  instagramUrl?: string;
  description?: string;
}

const Footer: React.FC<FooterProps> = ({
  storeName,
  phone,
  email,
  address,
  cnpj,
  primaryColor = '#4f46e5',
  instagramUrl,
  description
}) => {
  const currentYear = new Date().getFullYear();
  const [isTermsOpen, setIsTermsOpen] = useState(false);

  return (
    <footer
      className="bg-white border-t"
      style={{ borderTopColor: primaryColor, borderTopWidth: '1px' }}
    >
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center md:text-left">

          {/* Coluna 1: Nome da Loja + Descrição */}
          <div>
            <h3 className="font-semibold text-sm text-surface-900 mb-2">
              {storeName || 'Nossa Loja'}
            </h3>
            {description && (
              <p className="text-surface-600 text-xs leading-relaxed">
                {description}
              </p>
            )}
          </div>

          {/* Coluna 2: Email, Endereço, CNPJ */}
          <div className="space-y-2 flex flex-col items-center md:items-start">
            {email && (
              <a
                href={`mailto:${email}`}
                className="flex items-center justify-center md:justify-start gap-2 text-surface-700 hover:text-surface-900 transition"
              >
                <Mail size={16} style={{ color: primaryColor }} className="flex-shrink-0" />
                <span className="text-xs">{email}</span>
              </a>
            )}
            {address && (
              <div className="flex items-start justify-center md:justify-start gap-2 text-surface-700">
                <MapPin size={16} style={{ color: primaryColor }} className="flex-shrink-0 mt-0.5" />
                <span className="text-xs">{address}</span>
              </div>
            )}
            {cnpj && (
              <p className="text-surface-700 text-xs">
                <span className="font-semibold">CNPJ:</span> {cnpj}
              </p>
            )}
          </div>

          {/* Coluna 3: Instagram e WhatsApp */}
          <div className="space-y-2 flex flex-col items-center md:items-start">
            {instagramUrl && (
              <a
                href={instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded transition hover:opacity-90"
                style={{
                  backgroundColor: primaryColor,
                  color: 'white'
                }}
              >
                <Instagram size={16} />
                <span className="text-xs font-medium">Instagram</span>
              </a>
            )}
            {phone && (
              <a
                href={`https://wa.me/${phone.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center md:justify-start gap-2 text-surface-700 hover:text-surface-900 transition"
              >
                <Phone size={16} style={{ color: primaryColor }} className="flex-shrink-0" />
                <span className="text-xs">{phone}</span>
              </a>
            )}
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-surface-200 mt-4 pt-3 flex flex-col md:flex-row justify-between items-center gap-2">
          <div className="flex flex-col md:flex-row items-center gap-2 md:gap-4">
            <p className="text-surface-500 text-[10px] md:text-xs">
              © {currentYear} {storeName || 'Nossa Loja'}. Todos os direitos reservados.
            </p>
            <button
              onClick={() => setIsTermsOpen(true)}
              className="text-surface-400 hover:text-brand-600 text-[10px] md:text-xs font-medium transition-colors"
            >
              Termos de Uso
            </button>
          </div>
          <p className="text-surface-400 text-[10px] md:text-xs">
            Produto da família <a href="https://viniciusomatos.com/" target="_blank" rel="noreferrer" className="font-bold text-brand-600 hover:underline">Vinícius O Matos</a>
          </p>
        </div>
      </div>
      <CustomerTermsModal
        isOpen={isTermsOpen}
        onClose={() => setIsTermsOpen(false)}
        storeName={storeName}
      />
    </footer>
  );
};

export default Footer;
